import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Collapse,
  Chip,
  CircularProgress,
  useTheme,
  alpha,
  styled,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import DatePicker from 'components/DatePicker';
import { format, parse } from 'date-fns';
import { Search, ChevronDown, ChevronRight, Filter, RefreshCw, X } from 'lucide-react';

import { PageContainer, MinimalCard, CleanButton } from 'components/styled';
import { SearchInput } from 'components/SearchInput';
import StandardPageHeader from 'components/StandardPageHeader';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';
import { ActionTypeSelector } from './ActionTypeSelector';
import { useTenantDropdown } from 'TenantModule/hooks/useTenantDropdown';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { useTenants } from 'TenantModule/hooks/useTenants';
import { useActivityLogsQuery } from 'ActivityLogModule/query/hooks/useActivityLogsQuery';
import useUrl from 'hooks/useUrl';
import useAppNavigation from 'hooks/useAppNavigation';
import {
  ActivityLogSortBy,
  ActivityLogSource,
  ActivityLogSourceLabels,
  SortOrder,
} from 'ActivityLogModule/types/activityLog';
import {
  ActivityLogExpandableRow,
  TeamNavigationArgs,
} from 'ActivityLogModule/components/ActivityLogExpandableRow';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(4),
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
  borderRadius: '16px',
  border: '1px solid',
  borderColor:
    theme.palette.mode === 'dark' ? 'rgba(251, 146, 60, 0.3)' : 'rgba(251, 146, 60, 0.4)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset, 0 0 20px rgba(251, 146, 60, 0.15)'
      : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04), 0 0 20px rgba(251, 146, 60, 0.1)',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    borderColor:
      theme.palette.mode === 'dark' ? 'rgba(251, 146, 60, 0.5)' : 'rgba(251, 146, 60, 0.6)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset, 0 0 30px rgba(251, 146, 60, 0.25)'
        : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04), 0 0 30px rgba(251, 146, 60, 0.2)',
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-root': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.98)' : 'rgba(248, 250, 252, 0.98)',
    backdropFilter: 'blur(12px)',
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    color: theme.palette.mode === 'dark' ? 'rgba(203, 213, 225, 0.9)' : 'rgba(51, 65, 85, 0.9)',
    borderBottom:
      theme.palette.mode === 'dark'
        ? '2px solid rgba(148, 163, 184, 0.15)'
        : '2px solid rgba(226, 232, 240, 0.8)',
    padding: theme.spacing(2, 3),
    whiteSpace: 'nowrap',
    '&:first-of-type': {
      paddingLeft: theme.spacing(4),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(4),
    },
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.04)',
      '& .MuiTableCell-root': {
        borderBottomColor:
          theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
      },
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  '& .MuiTableCell-root': {
    fontSize: '0.875rem',
    padding: theme.spacing(2.5, 3),
    color: theme.palette.mode === 'dark' ? 'rgba(226, 232, 240, 0.95)' : 'rgba(30, 41, 59, 0.95)',
    borderBottom:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(148, 163, 184, 0.08)'
        : '1px solid rgba(226, 232, 240, 0.6)',
    transition: 'all 0.2s ease',
    '&:first-of-type': {
      paddingLeft: theme.spacing(4),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(4),
    },
  },
}));

const MinimalChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  fontWeight: 500,
  fontSize: '0.8125rem',
  height: 24,
  transition: 'all 0.15s ease',
  background: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.12),
  },
  '& .MuiChip-deleteIcon': {
    color: theme.palette.primary.main,
    fontSize: '1rem',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}));

const FilterSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'transparent',
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5'}`,
}));

/* ============================================================================
 * MAIN COMPONENT
 * ========================================================================== */

/**
 * ActivityLogPage - Displays paginated activity logs with comprehensive filtering
 *
 * @see .github/copilot-instructions.md#components--ui UI patterns
 */
export const ActivityLogPage: React.FC = () => {
  // URL params for pre-filtering
  const { queryParams: urlParams } = useUrl();
  const { navigateToTeamProfile } = useAppNavigation();
  const theme = useTheme();

  // Tenant/Realm hooks
  const { tenantOrder, tenantsHash } = useTenants({ isOff: false });
  const { openTenantDropdown } = useTenantDropdown();
  const { onSelectRealm } = useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);

  // Filter states (not submitted yet - only for UI)
  const [actorFilter, setActorFilter] = useState<string>('');
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<ActivityLogSource | ''>('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [teamIdInput, setTeamIdInput] = useState<string>('');
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [realmFilter, setRealmFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Submitted filters (used for actual query)
  const [submittedFilters, setSubmittedFilters] = useState({
    user: '',
    actionType: '',
    source: '' as ActivityLogSource | '',
    teamIds: [] as string[],
    tenant: '',
    realmId: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });

  // Pagination and sorting state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState<ActivityLogSortBy>(ActivityLogSortBy.occurredAt);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);

  // Expandable rows state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Filters expanded state
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (tenantFilter) count++;
    if (realmFilter) count++;
    if (actionTypeFilter) count++;
    if (sourceFilter) count++;
    if (startDate) count++;
    if (endDate) count++;
    if (teamIds.length > 0) count++;
    return count;
  }, [
    tenantFilter,
    realmFilter,
    actionTypeFilter,
    sourceFilter,
    startDate,
    endDate,
    teamIds.length,
  ]);

  // Build query params from SUBMITTED filters
  const queryParams = useMemo(
    () => ({
      user: submittedFilters.user || undefined,
      actionType: submittedFilters.actionType || undefined,
      source: submittedFilters.source || undefined,
      tenant: submittedFilters.tenant || undefined,
      realmId: submittedFilters.realmId || undefined,
      teamId: submittedFilters.teamIds.length > 0 ? submittedFilters.teamIds.join(',') : undefined,
      startDate: submittedFilters.startDate ? submittedFilters.startDate.toISOString() : undefined,
      endDate: submittedFilters.endDate ? submittedFilters.endDate.toISOString() : undefined,
      page: page + 1, // Backend uses 1-based indexing
      limit: rowsPerPage,
      sortBy,
      sortOrder,
    }),
    [submittedFilters, page, rowsPerPage, sortBy, sortOrder],
  );

  // Fetch activity logs
  const { activityLogs, isLoading } = useActivityLogsQuery(queryParams);

  // Pre-populate filters from URL params on mount
  useEffect(() => {
    const tenant = urlParams.get('tenant');
    const realmId = urlParams.get('realmId');
    const teamId = urlParams.get('teamId');
    const user = urlParams.get('user');
    const actionType = urlParams.get('actionType');

    // Only pre-populate if at least one param exists
    if (tenant || realmId || teamId || user || actionType) {
      if (tenant) {
        setTenantFilter(tenant);
      }
      if (realmId) {
        setRealmFilter(realmId);
      }
      if (teamId) {
        setTeamIds(teamId.split(','));
      }
      if (user) {
        setActorFilter(user);
      }
      if (actionType) {
        setActionTypeFilter(actionType);
      }

      // Auto-submit filters when coming from external link
      setSubmittedFilters({
        user: user || '',
        actionType: actionType || '',
        source: '',
        teamIds: teamId ? teamId.split(',') : [],
        tenant: tenant || '',
        realmId: realmId || '',
        startDate: null,
        endDate: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - urlParams intentionally excluded to avoid re-running

  // Team ID chip handlers
  const handleAddTeamIdChip = useCallback(() => {
    const value = teamIdInput.trim();
    if (!value) return;
    // Only add numeric team IDs
    if (!/^\d+$/.test(value)) return;
    if (teamIds.includes(value)) return; // Don't add duplicates
    setTeamIds([...teamIds, value]);
    setTeamIdInput('');
  }, [teamIdInput, teamIds]);

  const handleRemoveTeamIdChip = useCallback(
    (id: string) => {
      setTeamIds(teamIds.filter((teamId) => teamId !== id));
    },
    [teamIds],
  );

  // Search button handler - submits current filter state
  const handleSearch = useCallback(() => {
    setSubmittedFilters({
      user: actorFilter,
      actionType: actionTypeFilter,
      source: sourceFilter,
      teamIds: teamIds,
      tenant: tenantFilter,
      realmId: realmFilter,
      startDate,
      endDate,
    });
    setPage(0); // Reset to first page on new search
  }, [
    actorFilter,
    actionTypeFilter,
    sourceFilter,
    teamIds,
    tenantFilter,
    realmFilter,
    startDate,
    endDate,
  ]);

  // Tenant/Realm dropdown handlers
  const selectedTenantIdForFilter = useMemo(() => {
    if (!tenantFilter) return null;
    const match = tenantOrder.find((id) => tenantsHash[id]?.tenantName === tenantFilter);
    return match || null;
  }, [tenantFilter, tenantOrder, tenantsHash]);

  const onTenantSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    openTenantDropdown({
      target: e.currentTarget,
      shouldSkipSetTenant: true,
      onSelect: ({ tenant }) => {
        setTenantFilter(tenant.tenantName);
        setRealmFilter(''); // Clear realm when tenant changes
      },
    });
  };

  const onRealmSelectorClick: React.MouseEventHandler<HTMLElement> = (e) => {
    if (!selectedTenantIdForFilter) {
      // No tenant selected, cannot select realm
      return;
    }
    onSelectRealm({
      target: e.currentTarget,
      tenantId: selectedTenantIdForFilter,
      onSelect: ({ realm }) => setRealmFilter(realm.realm_id),
    });
  };

  const onClearTenant = () => {
    setTenantFilter('');
    setRealmFilter(''); // Clear realm when clearing tenant
  };

  const onClearRealm = () => {
    setRealmFilter('');
  };

  // Handlers
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleTeamClick = useCallback(
    (args: TeamNavigationArgs) => {
      // Navigate within the log row's own tenant/realm; the list spans tenants.
      navigateToTeamProfile(args);
    },
    [navigateToTeamProfile],
  );

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedIds(new Set()); // Clear expanded rows on page change
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedIds(new Set());
  }, []);

  const handleResetFilters = useCallback(() => {
    setActorFilter('');
    setActionTypeFilter('');
    setSourceFilter('');
    setTeamIds([]);
    setTeamIdInput('');
    setTenantFilter('');
    setRealmFilter('');
    setStartDate(null);
    setEndDate(null);
    setSubmittedFilters({
      user: '',
      actionType: '',
      source: '',
      teamIds: [],
      tenant: '',
      realmId: '',
      startDate: null,
      endDate: null,
    });
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (column: ActivityLogSortBy) => {
      if (sortBy === column) {
        // Toggle sort order if clicking the same column
        setSortOrder((prev) => (prev === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC));
      } else {
        // Set new column and default to DESC
        setSortBy(column);
        setSortOrder(SortOrder.DESC);
      }
      setPage(0); // Reset to first page when sorting changes
    },
    [sortBy],
  );

  return (
    <PageContainer>
      <Helmet title="Activity Log" />
      <StandardPageHeader title="Activity Log" />

      {/* Search and Quick Actions */}
      <MinimalCard $hoverEffect={false} mb={3}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <SearchInput
            fullWidth
            placeholder="Search by actor (username/email)..."
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            InputProps={{
              endAdornment: actorFilter && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setActorFilter('')}>
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 300 }}
          />

          <CleanButton
            variant="contained"
            startIcon={<Search size={18} />}
            onClick={handleSearch}
            disabled={isLoading}
          >
            Search
          </CleanButton>

          <CleanButton
            variant="outlined"
            startIcon={filtersExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            endIcon={
              activeFilterCount > 0 && (
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {activeFilterCount}
                </Box>
              )
            }
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            Filters
          </CleanButton>

          <CleanButton
            variant="outlined"
            startIcon={<RefreshCw size={18} />}
            onClick={handleResetFilters}
          >
            Reset
          </CleanButton>
        </Box>
      </MinimalCard>

      {/* Expandable Filters */}
      <Collapse in={filtersExpanded}>
        <MinimalCard $hoverEffect={false} mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Filter size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" fontWeight={600}>
                Advanced Filters
              </Typography>
            </Box>
            {activeFilterCount > 0 && (
              <CleanButton
                size="small"
                variant="text"
                startIcon={<X size={16} />}
                onClick={handleResetFilters}
              >
                Clear All
              </CleanButton>
            )}
          </Box>

          <FilterSection>
            {/* Row 1: Tenant, Realm */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2} mb={2}>
              <TenantSelector
                onClick={onTenantSelectorClick}
                tenantId={selectedTenantIdForFilter}
                onClear={onClearTenant}
              />
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={realmFilter || ''}
                ref={realmDropdownRef}
                onClear={onClearRealm}
              />
            </Box>

            {/* Row 2: Action Type, Source */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2} mb={2}>
              <ActionTypeSelector
                selectedActionType={actionTypeFilter}
                onSelectionChange={setActionTypeFilter}
              />
              <FormControl fullWidth>
                <InputLabel id="activity-log-source-filter-label">Source</InputLabel>
                <Select
                  labelId="activity-log-source-filter-label"
                  label="Source"
                  value={sourceFilter}
                  onChange={(e: SelectChangeEvent) =>
                    setSourceFilter(e.target.value as ActivityLogSource | '')
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="mcp">{ActivityLogSourceLabels.mcp}</MenuItem>
                  <MenuItem value="ui">{ActivityLogSourceLabels.ui}</MenuItem>
                  <MenuItem value="api">{ActivityLogSourceLabels.api}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Row 3: Start Date, End Date */}
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2} mb={2}>
              <DatePicker
                label="Start Date"
                value={startDate ? format(startDate, 'yyyy-MM-dd') : null}
                onChange={(newDate) =>
                  setStartDate(newDate ? parse(newDate, 'yyyy-MM-dd', new Date()) : null)
                }
                fullWidth
                size="small"
              />
              <DatePicker
                label="End Date"
                value={endDate ? format(endDate, 'yyyy-MM-dd') : null}
                onChange={(newDate) =>
                  setEndDate(newDate ? parse(newDate, 'yyyy-MM-dd', new Date()) : null)
                }
                fullWidth
                size="small"
              />
            </Box>

            {/* Row 4: Team IDs (full width) */}
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Team IDs"
                placeholder="Enter ID and press Enter"
                value={teamIdInput}
                onChange={(e) => setTeamIdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTeamIdChip();
                  }
                }}
              />
              {teamIds.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                  {teamIds.map((id) => (
                    <MinimalChip
                      key={id}
                      label={id}
                      onDelete={() => handleRemoveTeamIdChip(id)}
                      size="small"
                    />
                  ))}
                </Box>
              )}
            </Box>
          </FilterSection>
        </MinimalCard>
      </Collapse>

      {/* Table Section */}
      <StyledTableContainer>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.occurredAt}
                      direction={
                        sortBy === ActivityLogSortBy.occurredAt
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.occurredAt)}
                    >
                      TIMESTAMP
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.username}
                      direction={
                        sortBy === ActivityLogSortBy.username
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.username)}
                    >
                      ACTOR
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>EMAIL</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.actionType}
                      direction={
                        sortBy === ActivityLogSortBy.actionType
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.actionType)}
                    >
                      ACTION TYPE
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>SUMMARY</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.tenant}
                      direction={
                        sortBy === ActivityLogSortBy.tenant
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.tenant)}
                    >
                      TENANT
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === ActivityLogSortBy.realmId}
                      direction={
                        sortBy === ActivityLogSortBy.realmId
                          ? (sortOrder.toLowerCase() as 'asc' | 'desc')
                          : 'desc'
                      }
                      onClick={() => handleSort(ActivityLogSortBy.realmId)}
                    >
                      REALM
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>TEAM ID</TableCell>
                </TableRow>
              </StyledTableHead>
              <StyledTableBody>
                {activityLogs?.data && activityLogs.data.length > 0 ? (
                  activityLogs.data.map((log) => (
                    <ActivityLogExpandableRow
                      key={log.id}
                      log={log}
                      isExpanded={expandedIds.has(log.id)}
                      onToggle={() => handleToggleExpand(log.id)}
                      onTeamClick={handleTeamClick}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary" py={4}>
                        No activity logs found for the selected filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </StyledTableBody>
            </Table>

            {activityLogs?.pagination && (
              <Box
                sx={{
                  borderTop: (theme) =>
                    theme.palette.mode === 'dark'
                      ? '1px solid rgba(148, 163, 184, 0.12)'
                      : '1px solid rgba(226, 232, 240, 0.6)',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(0deg, rgba(30, 41, 59, 0.4) 0%, transparent 100%)'
                      : 'linear-gradient(0deg, rgba(248, 250, 252, 0.6) 0%, transparent 100%)',
                }}
              >
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  component="div"
                  count={activityLogs.pagination.total}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Box>
            )}
          </>
        )}
      </StyledTableContainer>
    </PageContainer>
  );
};

export default ActivityLogPage;
